<template>
  <div
    class="qty-stepper"
    :class="{
      'qty-stepper--compact': compact,
      'qty-stepper--vertical': vertical,
    }"
  >
    <!--
      VERTICAL puts increment ABOVE the count and decrement BELOW it, which
      is a DOM-ORDER change and not a CSS one. `flex-direction: column-reverse`
      would look identical and be wrong for tab order and for a screen
      reader, both of which follow the DOM. So each button is rendered in its
      arrangement's position rather than reordered by style.

      The duplication is two small `v-if` blocks rather than a computed list,
      because the alternative -- one loop over a [-1, +1] array -- would make
      the aria-label, the testid and the handler all indirect, on a control
      whose whole job is being unambiguous.
    -->
    <button
      v-if="vertical"
      type="button"
      class="qty-stepper__btn qty-stepper__btn--add qty-stepper__no-callout"
      :disabled="incrementDisabled"
      :aria-label="`Increase quantity for size ${sizeLabel}`"
      :data-testid="incrementTestid"
      @pointerdown="startRepeat(1, $event)"
      @pointerup="stopRepeat"
      @pointercancel="stopRepeat"
      @pointerleave="stopRepeat"
    >
      <span class="tf-icon" aria-hidden="true">add</span>
    </button>

    <button
      v-if="!vertical"
      type="button"
      class="qty-stepper__btn qty-stepper__no-callout"
      :disabled="decrementDisabled"
      :aria-label="`Decrease quantity for size ${sizeLabel}`"
      :data-testid="decrementTestid"
      @pointerdown="startRepeat(-1, $event)"
      @pointerup="stopRepeat"
      @pointercancel="stopRepeat"
      @pointerleave="stopRepeat"
    >
      <span class="tf-icon" aria-hidden="true">remove</span>
    </button>

    <!--
      A REAL number input, not a button that opens a custom keypad.

      Jake, 2026-09-10: "We don't have to use an in-layout keypad as long as
      the OS keyboard will dismiss when blurring the text field. We do need
      the +/- buttons and numeric entry to work." That killed locked
      decisions 4, 5, 6 and 12; see
      docs/decisions/2026-09-11-mobile-quantity-entry-scope.md.

      There is deliberately NO @blur / @focus / @keydown handler here.
      Native blur dismisses the OS keyboard unaided, and a speculative
      handler is how that gets broken.

      `max`, never `maxlength`: maxlength is unreliable on type=number.
    -->
    <input
      ref="field"
      type="number"
      min="0"
      max="9999"
      step="1"
      inputmode="numeric"
      placeholder="0"
      class="qty-stepper__input qty-stepper__no-callout"
      :class="{ 'is-set': hasQuantity }"
      :aria-label="`Quantity for size ${sizeLabel}`"
      :value="modelValue == null ? '' : modelValue"
      :disabled="disabled"
      :data-testid="qtyTestid"
      data-stepper-field="qty-stepper-input"
      @input="onInput"
    />

    <button
      v-if="!vertical"
      type="button"
      class="qty-stepper__btn qty-stepper__btn--add qty-stepper__no-callout"
      :disabled="incrementDisabled"
      :aria-label="`Increase quantity for size ${sizeLabel}`"
      :data-testid="incrementTestid"
      @pointerdown="startRepeat(1, $event)"
      @pointerup="stopRepeat"
      @pointercancel="stopRepeat"
      @pointerleave="stopRepeat"
    >
      <span class="tf-icon" aria-hidden="true">add</span>
    </button>

    <button
      v-if="vertical"
      type="button"
      class="qty-stepper__btn qty-stepper__no-callout"
      :disabled="decrementDisabled"
      :aria-label="`Decrease quantity for size ${sizeLabel}`"
      :data-testid="decrementTestid"
      @pointerdown="startRepeat(-1, $event)"
      @pointerup="stopRepeat"
      @pointercancel="stopRepeat"
      @pointerleave="stopRepeat"
    >
      <span class="tf-icon" aria-hidden="true">remove</span>
    </button>
  </div>
</template>

<script>
/**
 * The shared - / quantity / + control (locked decisions 2, 3, 9, 21).
 *
 * One definition serves the mobile PDP size rows and the cart lines, so a
 * shopper fixing a wrong count meets the same control in both places.
 *
 * TWO PROTOTYPE BUGS ARE DELIBERATELY NOT REPRODUCED HERE. The design
 * prototype (`design/qty-shared.jsx`, `QStep` at :107-124) binds
 * `onMouseDown` AND `onTouchStart`, so on a touch device a single tap fires
 * both and increments twice; and it handles `onTouchEnd` but not
 * `onTouchCancel`, so an OS-interrupted touch leaves the repeat timer
 * running. Pointer events fix the first by construction (the browser emits
 * one pointer stream whatever the hardware), and `pointercancel` fixes the
 * second. Both are pinned by tests in
 * `__tests__/quantity-stepper.spec.js`.
 */

// Acceleration constants are the prototype's, locked by decision 3.
const FIRST_REPEAT_MS = 340;
const DECAY = 0.72;
const FLOOR_MS = 60;
const MAX_QTY = 9999;

export default {
  name: "QuantityStepper",

  props: {
    /** Current quantity. `null` means ABSENT, which is not the same as 0. */
    modelValue: {
      type: Number,
      default: null,
    },
    /** Names the control for screen readers; also shown by the caller. */
    sizeLabel: {
      type: String,
      default: "",
    },
    /**
     * Testid for the quantity field.
     *
     * The PDP passes its EXISTING `sf-product-qty-<sizeId>` through so the
     * mobile field stays addressable by the id desktop already uses -- a
     * ui-runner scenario that `fill`s it keeps working across the
     * breakpoint. Defaults to the component's own id for standalone use.
     */
    qtyTestid: {
      type: String,
      default: "qty-stepper-input",
    },
    /**
     * Testids for the two BUTTONS, when more than one stepper is on screen.
     *
     * The defaults are fine while a page has a single stepper, but the batch
     * order editor renders one PER SIZE, and ui-runner selectors are exact
     * `data-testid=` matches with no globbing and no nth -- so eight
     * identical `qty-stepper-increment` nodes would be unaddressable, and a
     * scenario could not say "add one XL". The caller passes a per-instance
     * pair; the storefront consumers keep the defaults.
     */
    incrementTestid: {
      type: String,
      default: "qty-stepper-increment",
    },
    decrementTestid: {
      type: String,
      default: "qty-stepper-decrement",
    },
    /**
     * Cart lines floor at 1 -- removing a line is what `co-remove` is for,
     * and a 0 there would silently delete the row the shopper is editing.
     * The PDP floors at 0, where 0 means "not ordered".
     */
    minValue: {
      type: Number,
      default: 0,
    },
    /** Disables every control (in-flight cart write, read-only preview). */
    disabled: {
      type: Boolean,
      default: false,
    },
    /** Tighter metrics for the cart's narrow column. */
    compact: {
      type: Boolean,
      default: false,
    },
    /**
     * Stack the control: increment ABOVE the count, decrement BELOW.
     *
     * Jake, 2026-09-22, for the batch order editor: sizes run HORIZONTALLY
     * with "+/- buttons above and below each quantity", which lets eight
     * sizes occupy one band instead of eight stacked rows. The storefront
     * consumers keep the default horizontal arrangement.
     */
    vertical: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["update:modelValue"],

  data() {
    return { timer: null };
  },

  computed: {
    numericValue() {
      const n = Number(this.modelValue);
      return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
    },

    /**
     * Deliberately a computed rather than an inline `numericValue > 0` in
     * the template. The `vuetify3-input-sweep` spec locates an `@input`'s
     * host tag by scanning backwards for the nearest `<` with no `>`
     * between -- so a bare `>` inside an attribute expression hides the
     * `<input` from it and the native input is misreported as a Vuetify
     * component. Keeping the comparison out of the template keeps that
     * codebase-wide guard meaningful instead of making it noisy.
     */
    hasQuantity() {
      return this.numericValue > 0;
    },

    decrementDisabled() {
      return this.disabled || this.numericValue <= this.minValue;
    },

    incrementDisabled() {
      return this.disabled || this.numericValue >= MAX_QTY;
    },
  },

  // A repeat timer that outlives the component writes into a dead instance.
  beforeUnmount() {
    this.stopRepeat();
  },

  methods: {
    /**
     * Press-and-hold: fire once immediately, then repeat with acceleration
     * until the pointer is released, cancelled, or leaves the button.
     */
    startRepeat(direction, event) {
      // Only the primary button; a right-click must not start a repeat.
      if (event && typeof event.button === "number" && event.button !== 0) {
        return;
      }
      if (direction < 0 ? this.decrementDisabled : this.incrementDisabled) {
        return;
      }

      this.stopRepeat();

      // The repeat counts from a LOCAL cursor rather than re-reading
      // `modelValue` each tick. The parent's write-back is asynchronous
      // (a Vue prop update lands on the next flush, and a cart line's
      // lands after an HTTP round trip), so a tick that re-read the prop
      // would emit the same number repeatedly for the whole hold.
      let cursor = this.numericValue;
      const step = () => {
        const next = cursor + direction;
        if (next < this.minValue || next > MAX_QTY) return false;
        cursor = next;
        this.$emit("update:modelValue", next);
        return true;
      };

      if (!step()) return;

      let delay = FIRST_REPEAT_MS;
      const tick = () => {
        // A hold that reaches the floor or the ceiling stops THERE.
        // Without this the timer spins forever, re-emitting the boundary
        // value for as long as the finger stays down.
        if (this.disabled || !step()) {
          this.stopRepeat();
          return;
        }
        delay = Math.max(FLOOR_MS, delay * DECAY);
        this.timer = setTimeout(tick, delay);
      };
      this.timer = setTimeout(tick, delay);
    },

    stopRepeat() {
      if (this.timer != null) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    },

    /**
     * An emptied field emits `null`, not 0: "zero means absent" (decision
     * 9) is about the size leaving the order, and a stored 0 is a
     * different thing that would survive into the cart payload.
     */
    onInput(event) {
      const raw = event && event.target ? event.target.value : "";
      if (raw === "" || raw == null) {
        this.$emit("update:modelValue", null);
        return;
      }
      const parsed = Math.floor(Number(raw));
      if (!Number.isFinite(parsed)) {
        this.$emit("update:modelValue", null);
        return;
      }
      const clamped = Math.min(MAX_QTY, Math.max(this.minValue, parsed));
      this.$emit("update:modelValue", clamped);
    },
  },
};
</script>

<style scoped>
.qty-stepper {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* 48px clears the 44px hit-target floor in ADR 0028 (decision 2). */
.qty-stepper__btn {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid var(--tf-border, #e0e0e0);
  border-radius: 12px;
  background: #fff;
  color: var(--tf-primary, #119ae9);
  cursor: pointer;
  /* Without this, a press-and-hold pans the page instead of repeating. */
  touch-action: manipulation;
}

.qty-stepper__btn:disabled {
  color: #c4d0da;
  border-color: var(--tf-border-soft, #eef2f5);
  cursor: default;
}

.qty-stepper__input {
  width: 100%;
  min-width: 0;
  height: 48px;
  text-align: center;
  padding: 8px 4px;
  border: 1px solid var(--tf-border, #e0e0e0);
  border-radius: 8px;
  font-size: 16px; /* < 16px makes iOS Safari zoom the page on focus. */
  box-sizing: border-box;
  outline: none;
  color: var(--tf-fg-1, #212121);
}

.qty-stepper__input.is-set {
  border-color: var(--tf-primary, #119ae9);
}

/* The prototype guards only the stepper button, so a long-press on the
   quantity itself still raises the iOS text-selection callout. Guard all
   three. */
.qty-stepper__no-callout {
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
}

/* The field is the one place selection must still work -- a shopper
   retyping a count needs the caret. */
.qty-stepper__input.qty-stepper__no-callout {
  user-select: text;
  -webkit-user-select: text;
}

/*
  Vertical: + / count / - in a narrow column, so a row of sizes reads across
  the card. The field loses its stretch -- in a column `width: 100%` would
  make it as wide as the cell rather than as wide as the buttons.
*/
.qty-stepper--vertical {
  flex-direction: column;
  gap: 4px;
}

.qty-stepper--vertical .qty-stepper__input {
  width: 48px;
  max-width: 48px;
}

.qty-stepper--vertical.qty-stepper--compact .qty-stepper__input {
  width: 44px;
  max-width: 44px;
}

.qty-stepper--compact {
  gap: 4px;
}

.qty-stepper--compact .qty-stepper__btn {
  width: 44px;
  height: 44px;
}

.qty-stepper--compact .qty-stepper__input {
  height: 44px;
  max-width: 64px;
}
</style>
